<template>
  <div>
    <div v-if="canCreateLoan" class="flex justify-end mb-3">
      <Button @click="showCreateLoanModal" class="bg-theme-1 text-white">
        Зээл нэмэх
      </Button>
    </div>

    <CustomerLoans
      :loading="false"
      :rows="customer.loans"
      :columns="columns"
      :line-numbers="true"
    />

    <ModalEdit
      :id="createLoanModalId"
      title="Зээл нэмэх"
      :loading="isCreatingLoan"
      @click="onCreateLoan"
    >
      <div class="p-5">
        <InputMoney
          v-model="$v.form.amount.$model"
          :vuelidate="$v.form.amount"
          :label="$t('text.loanAmount')"
        />
        <InputString
          v-model.trim="$v.form.interestRate.$model"
          :vuelidate="$v.form.interestRate"
          :label="$t('text.loanInterestRate')"
          type="number"
          class="mt-3"
        />
        <InputDate
          v-model="$v.form.startDate.$model"
          :vuelidate="$v.form.startDate"
          :label="$t('text.startDate')"
          class="mt-3"
        />
        <InputDate
          v-model="$v.form.endDate.$model"
          :vuelidate="$v.form.endDate"
          :label="$t('text.endDate')"
          class="mt-3"
        />
      </div>
    </ModalEdit>
  </div>
</template>

<script>
import { required, minValue } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { CUSTOMER_REQUEST } from '@/store/actions/customer';
import { ROLES } from '@/constants/roles';
import Repository, { CUSTOMERS } from '@/repositories/RepositoryFactory';
import CustomerLoans from '@/components/customer/CustomerLoans';
import Button from '@/components/ui/button/Button';
import ModalEdit from '@/components/ui/ModalEdit';
import InputMoney from '@/components/ui/input/InputMoney';
import InputString from '@/components/ui/input/InputString';
import InputDate from '@/components/ui/input/InputDate';

const CustomersRepository = Repository.get(CUSTOMERS);

export default {
  mixins: [validationMixin],

  props: {
    customer: Object,
  },

  validations() {
    return {
      form: {
        amount: { required, minValue: minValue(1) },
        interestRate: { required, minValue: minValue(0) },
        startDate: { required },
        endDate: { required },
      },
    };
  },

  data() {
    return {
      createLoanModalId: 'create-loan-modal',
      isCreatingLoan: false,
      form: {
        amount: null,
        interestRate: '',
        startDate: '',
        endDate: '',
      },
      columns: [
        {
          label: this.$t('text.loanNumber'),
          field: 'id',
          filterable: true,
        },
        {
          label: this.$t('text.loanAmount'),
          field: 'amount',
        },
        {
          label: this.$t('text.loanInterestRate'),
          field: 'interestRate',
        },
        {
          label: this.$t('text.loanAmountRate'),
          field: 'rateAmount',
        },
        {
          label: this.$t('text.loanAmountIncreaseRate'),
          field: 'increaseRateAmount',
        },
        {
          label: this.$t('text.loanAmountPay'),
          field: 'payAmount',
        },
        {
          label: this.$t('text.loanAmountPaid'),
          field: 'paidAmount',
        },
        {
          label: this.$t('text.loanDate'),
          field: 'startDate',
        },
        {
          label: this.$t('text.loanDateRepayment'),
          field: 'endDate',
        },
        {
          label: this.$t('text.loanDatePaid'),
          field: 'paidDate',
        },
        {
          label: this.$t('text.status'),
          field: 'status',
        },
        {
          label: this.$t('table.action'),
          field: 'action',
        },
      ],
    };
  },

  computed: {
    canCreateLoan() {
      return this.$store.getters.me?.role === ROLES.ADMINISTRATOR;
    },
  },

  methods: {
    showCreateLoanModal() {
      this.resetForm();
      cash(`#${this.createLoanModalId}`).modal('show');
    },
    resetForm() {
      this.form = {
        amount: null,
        interestRate: '',
        startDate: '',
        endDate: '',
      };
      this.$v.$reset();
    },
    async onCreateLoan() {
      if (this.isCreatingLoan) {
        return;
      }
      this.$v.$touch();
      if (this.$v.$anyError) {
        this.$toasted.show(this.$t('toast.failedForm'), { type: 'error' });
        return;
      }

      this.isCreatingLoan = true;
      try {
        await CustomersRepository.createLoan(this.customer.id, {
          amount: this.form.amount,
          interest_rate: Number(this.form.interestRate),
          start_date: this.form.startDate,
          end_date: this.form.endDate,
        });

        this.$toasted.show(this.$t('toast.success'), { type: 'success' });
        cash(`#${this.createLoanModalId}`).modal('hide');
        this.resetForm();

        // Refresh data after closing modal to avoid UI freeze.
        await this.$store.dispatch(CUSTOMER_REQUEST, this.$route.params.customerId);
      } catch (error) {
        this.$toasted.show(error.message, { type: 'error' });
      } finally {
        this.isCreatingLoan = false;
      }
    },
  },

  components: {
    CustomerLoans,
    Button,
    ModalEdit,
    InputMoney,
    InputString,
    InputDate,
  },
};
</script>
